import { saveAs } from "file-saver";
import { axiosInstance } from "./index";
import type { StatementStatus, PayeeEntityType } from "./paymentStatement";

export type FinancePaymentStatus =
	| "not_started"
	| "submitted"
	| "in_review"
	| "in_process"
	| "on_hold"
	| "completed"
	| "failed"
	| "rejected_by_finance";

/** Filters for the cross-campaign Finance list. Arrays mean "any of". Dates are YYYY-MM-DD inclusive. */
export interface AdminPaymentsFilters {
	q?: string;
	paymentStatus?: FinancePaymentStatus[];
	statementStatus?: StatementStatus[];
	country?: string[];
	currency?: string[];
	issuedFrom?: string;
	issuedTo?: string;
	paidFrom?: string;
	paidTo?: string;
}

/** One row: every money and payee value is the frozen statement; payment status is the instruction's. */
export interface AdminPaymentRow {
	statementId: string;
	statementNumber: string;
	issuedAt: string;
	creatorLegalName: string;
	entityType: PayeeEntityType;
	registrationNumberLabel: string | null;
	/** Country of tax residence on the statement. */
	countryCode: string | null;
	campaignId: string;
	campaignName: string;
	projectCode: string;
	principalAmount: number;
	taxName: string | null;
	/** Fraction, e.g. 0.1. Null when no tax applied. */
	taxRate: number | null;
	declaredTaxAmount: number | null;
	amountPayable: number;
	currency: string;
	statementStatus: StatementStatus;
	paymentInstructionId: string;
	paymentStatus: FinancePaymentStatus;
	/** Why Finance put the payment on hold; null unless on_hold. */
	paymentStatusReason: string | null;
	paidAt: string | null;
	transactionReference: string | null;
	bankName: string;
	accountLast4: string;
}

export interface AdminPaymentsList {
	items: AdminPaymentRow[];
	total: number;
	limit: number;
	offset: number;
}

/** Query params exactly as the backend parses them: lists comma-joined, empties dropped. */
export const toAdminPaymentsParams = (filters: AdminPaymentsFilters, page?: { limit: number; offset: number }) => {
	const params: Record<string, string | number> = {};
	for (const [key, value] of Object.entries(filters)) {
		if (Array.isArray(value)) {
			if (value.length) params[key] = value.join(",");
		} else if (typeof value === "string" && value.trim()) {
			params[key] = value.trim();
		}
	}
	if (page) {
		params.limit = page.limit;
		params.offset = page.offset;
	}
	return params;
};

const filenameFrom = (contentDisposition: unknown, fallback: string): string => {
	const match = typeof contentDisposition === "string" ? /filename="([^"]+)"/.exec(contentDisposition) : null;
	return match?.[1] || fallback;
};

export const adminPaymentsAPI = {
	list: async (filters: AdminPaymentsFilters, page: { limit: number; offset: number }): Promise<AdminPaymentsList> =>
		(await axiosInstance.get("/admin/payments", { params: toAdminPaymentsParams(filters, page) })).data,
	/** Downloads the filtered rows as CSV (generated and audited server-side). */
	exportCsv: async (filters: AdminPaymentsFilters): Promise<void> => {
		const response = await axiosInstance.get("/admin/payments/export.csv", {
			params: toAdminPaymentsParams(filters),
			responseType: "blob",
		});
		const fallback = `payment-statements-${new Date().toISOString().slice(0, 10)}.csv`;
		saveAs(
			new Blob([response.data], { type: "text/csv;charset=utf-8" }),
			filenameFrom(response.headers?.["content-disposition"], fallback),
		);
	},
};
